import {useAppDispatch, useAppSelector, useAppStore} from "../../hooks/ReduxHooks";
import React, {FC, useRef} from "react";
import {PhotoFolderDto} from "../../services/photos/dto/PhotoFolderDto";
import {WrapPanel} from "../panels/WrapPanel";
import styled from "@emotion/styled";
import {css} from "@emotion/css";
import {ContentLocation} from "../../locations/ContentLocation";
import classNames from "classnames";
import {ContextMenu} from "../common/ContextMenu";
import {DropDownMenuItem} from "../toolbar/DropDownMenu";
import {PhotosActions} from "../../redux/PhotosSlice";
import {PhotoBrowserService} from "../../services/photos/PhotoBrowserService";
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import DoneIcon from '@mui/icons-material/Done';
import FolderIcon from '@mui/icons-material/Folder';
import RecursivelyExcludedFolderIcon from '@mui/icons-material/FolderDelete';

export const horizontalWrapPanelStyle = css`
    flex-direction: row !important;
    flex-wrap: wrap !important;
    justify-content: initial !important;
    overflow: visible !important;
`;

export const PhotoFolders = () => {
    const folders = useAppSelector(s => s.photosState.folders);

    const menuRef = useRef<HTMLDivElement>(null);

    const dispatch = useAppDispatch();

    const store = useAppStore();

    async function updateExclusions(exclude: boolean, excludeRecursively: boolean) {
        const state = store.getState().photosState;
        if (!state.contextFolder) return;

        const breadcrumbs = [...state.breadcrumbs, state.contextFolder];
        await new PhotoBrowserService(dispatch).updateExclusions(breadcrumbs, exclude, excludeRecursively);

        dispatch(PhotosActions.setContextFolder(undefined));
    }

    const contextMenuItems: (DropDownMenuItem | string)[] = [
        {
            icon: <DoneIcon/>,
            name: "Show",
            onClick: async () => {
                await updateExclusions(false, false);
            },
        }, "sep1", {
            icon: <DeleteIcon/>,
            name: "Exclude",
            onClick: async () => {
                await updateExclusions(true, false);
            },
        }, {
            icon: <DeleteForeverIcon/>,
            name: "Exclude (Recursively)",
            onClick: async () => {
                await updateExclusions(false, true);
            },
        }];

    return (
        <>
            <WrapPanel className={horizontalWrapPanelStyle} elementRef={menuRef}>
                {folders.map((folder, i) => <PhotoFolder key={i} folder={folder}/>)}
            </WrapPanel>
            <ContextMenu menuItems={contextMenuItems}
                         elementRef={menuRef}/>
        </>
    );
};

const FolderDiv = styled.div`
    width: 200px;
    padding-left: 5px;
    display: flex;
    align-items: center;

    &.excluded, &.excluded-recursive {
        color: grey;
    }

    svg {
        width: 42px;
        height: 42px;
        margin-right: 5px;
    }
`;

const PhotoFolder: FC<{ folder: PhotoFolderDto }> = ({folder}) => {
    const breadcrumbs = useAppSelector(s => s.photosState.breadcrumbs);

    const isSelected = useAppSelector(s => s.photosState.contextFolder === folder.Name);

    const dispatch = useAppDispatch();

    const folderClasses = classNames("selectable", "pointer", {
        "excluded": folder.Excluded,
        "excluded-recursive": folder.ExcludedRecursive,
        "selected": isSelected,
    });

    let title;
    if (folder.ExcludedRecursive) {
        title = "This folder has been excluded recursively from the photo frame";
    } else if (folder.Excluded) {
        title = "This folder has been excluded from the photo frame";
    } else {
        title = "This folder will be shown in the photo frame";
    }

    let icon = <FolderIcon/>;
    if (folder.ExcludedRecursive) {
        icon = <RecursivelyExcludedFolderIcon/>;
    } else if (folder.Excluded) {
        icon = <ExcludedFolderIcon/>;
    }
    return (
        <FolderDiv className={folderClasses}
                   title={title}
                   onClick={() => ContentLocation.showPhotoFolder(folder.Breadcrumbs ?? breadcrumbs, folder)}
                   onContextMenu={() => dispatch(PhotosActions.setContextFolder(folder.Name))}
        >
            {icon}
            {folder.Name + (folder.Breadcrumbs?.length ? ` (${folder.Breadcrumbs.join("/")})` : "")}
        </FolderDiv>
    )
}

const ExcludedFolderIcon = () => (
    <svg className="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium"
         focusable="false"
         aria-hidden="true"
         viewBox="0 0 24 24"
         fill="currentColor"
         data-testid="ExcludedFolderIcon">
        <path
            d="M20 6h-8l-2-2H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zM7.83 16zm9.58-3L19 14.59 17.59 16 16 14.41 14.41 16 13 14.59 14.59 13 13 11.41 14.41 10 16 11.59 17.59 10 19 11.41 17.41 13z"></path>
    </svg>
);
